import React, { useState, useEffect } from 'react';
import { Card, DatePicker, Button, Space, Typography, Statistic, Row, Col, Alert, Divider, Modal } from 'antd';
import { DeleteOutlined, WarningOutlined, DatabaseOutlined } from '@ant-design/icons';
import { RefreshIcon } from '../../components/common/ActionIcons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import PageWrapper from '../../components/common/PageWrapper';
import {
    getDataManagementStatistics,
    previewSubjects,
    previewUploadBatches,
    previewPatients,
    clearSubjects,
    clearUploadBatches,
    clearPatients,
    resetPreviewSubjects,
    resetPreviewUploadBatches,
    resetPreviewPatients,
    resetClearSubjects,
    resetClearUploadBatches,
    resetClearPatients,
} from '../../actions/dataManagementActions';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const DataManagementScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [subjectDateRange, setSubjectDateRange] = useState(null);
  const [uploadBatchDateRange, setUploadBatchDateRange] = useState(null);
  const [patientDateRange, setPatientDateRange] = useState(null);
  const [subjectModalVisible, setSubjectModalVisible] = useState(false);
  const [uploadBatchModalVisible, setUploadBatchModalVisible] = useState(false);
  const [patientModalVisible, setPatientModalVisible] = useState(false);

  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const isSuperUser = userInfo?.roles?.includes('SuperUser');

  const statisticsState = useSelector((state) => state.dataManagementStatistics);
  const previewSubjectsState = useSelector((state) => state.dataManagementPreviewSubjects);
  const previewUploadBatchesState = useSelector((state) => state.dataManagementPreviewUploadBatches);
  const clearSubjectsState = useSelector((state) => state.dataManagementClearSubjects);
  const clearUploadBatchesState = useSelector((state) => state.dataManagementClearUploadBatches);
  const previewPatientsState = useSelector((state) => state.dataManagementPreviewPatients);
  const clearPatientsState = useSelector((state) => state.dataManagementClearPatients);

  useEffect(() => {
    if (!isSuperUser) {
      navigate('/unauthorized');
      return;
    }
    dispatch(getDataManagementStatistics());
  }, [isSuperUser, navigate, dispatch]);

  useEffect(() => {
    if (clearSubjectsState.success) {
      setSubjectDateRange(null);
      dispatch(resetPreviewSubjects());
      dispatch(getDataManagementStatistics());
      // Reset success state after 5 seconds to allow user to see the message
      const timer = setTimeout(() => {
        dispatch(resetClearSubjects());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [clearSubjectsState.success, dispatch]);

  useEffect(() => {
    if (clearUploadBatchesState.success) {
      setUploadBatchDateRange(null);
      dispatch(resetPreviewUploadBatches());
      dispatch(getDataManagementStatistics());
      // Reset success state after 5 seconds to allow user to see the message
      const timer = setTimeout(() => {
        dispatch(resetClearUploadBatches());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [clearUploadBatchesState.success, dispatch]);

  useEffect(() => {
    if (clearPatientsState.success) {
      setPatientDateRange(null);
      dispatch(resetPreviewPatients());
      dispatch(getDataManagementStatistics());
      const timer = setTimeout(() => {
        dispatch(resetClearPatients());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [clearPatientsState.success, dispatch]);

  const setDateRangeShortcut = (type, setter) => {
    const now = moment();
    let startDate, endDate;

    switch (type) {
      case '24h':
        startDate = now.clone().subtract(24, 'hours');
        endDate = now;
        break;
      case '7d':
        startDate = now.clone().subtract(7, 'days').startOf('day');
        endDate = now.endOf('day');
        break;
      case '30d':
        startDate = now.clone().subtract(30, 'days').startOf('day');
        endDate = now.endOf('day');
        break;
      case '90d':
        startDate = now.clone().subtract(90, 'days').startOf('day');
        endDate = now.endOf('day');
        break;
      case 'month':
        startDate = now.clone().startOf('month');
        endDate = now.endOf('month');
        break;
      case 'lastMonth':
        startDate = now.clone().subtract(1, 'month').startOf('month');
        endDate = now.clone().subtract(1, 'month').endOf('month');
        break;
      default:
        return;
    }

    setter([startDate, endDate]);
  };

  const handlePreviewSubjects = () => {
    if (!subjectDateRange) {
      return;
    }
    dispatch(previewSubjects(
      subjectDateRange[0].toISOString(),
      subjectDateRange[1].toISOString()
    ));
  };

  const handlePreviewUploadBatches = () => {
    if (!uploadBatchDateRange) {
      return;
    }
    dispatch(previewUploadBatches(
      uploadBatchDateRange[0].toISOString(),
      uploadBatchDateRange[1].toISOString()
    ));
  };

  const handlePreviewPatients = () => {
    if (!patientDateRange) {
      return;
    }
    dispatch(previewPatients(
      patientDateRange[0].toISOString(),
      patientDateRange[1].toISOString()
    ));
  };

  const handleClearSubjects = () => {
    if (!subjectDateRange) {
      return;
    }
    setSubjectModalVisible(true);
  };

  const handleConfirmClearSubjects = () => {
    dispatch(clearSubjects(
      subjectDateRange[0].toISOString(),
      subjectDateRange[1].toISOString()
    ));
    setSubjectModalVisible(false);
  };

  const handleClearUploadBatches = () => {
    if (!uploadBatchDateRange) {
      return;
    }
    setUploadBatchModalVisible(true);
  };

  const handleConfirmClearUploadBatches = () => {
    dispatch(clearUploadBatches(
      uploadBatchDateRange[0].toISOString(),
      uploadBatchDateRange[1].toISOString()
    ));
    setUploadBatchModalVisible(false);
  };

  const handleClearPatients = () => {
    if (!patientDateRange) {
      return;
    }
    setPatientModalVisible(true);
  };

  const handleConfirmClearPatients = () => {
    dispatch(clearPatients(
      patientDateRange[0].toISOString(),
      patientDateRange[1].toISOString()
    ));
    setPatientModalVisible(false);
  };

  return (
    <PageWrapper
      title="Data Management"
      subtitle={
        <Text type="secondary" style={{ fontSize: '14px' }}>
          Clear data within specific date ranges. This operation is irreversible.
        </Text>
      }
      icon={<DatabaseOutlined style={{ fontSize: 24 }} />}
      showCard={false}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>

        <Alert
          message="SuperUser Only"
          description="This feature is only accessible to SuperUsers. Use with caution as deletions are permanent and cannot be undone."
          type="warning"
          showIcon
          icon={<WarningOutlined />}
        />

        {statisticsState.statistics && (
          <Card title={<Space><DatabaseOutlined /> Database Statistics</Space>}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="Total Subjects" value={statisticsState.statistics.totalSubjects} />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="Total Upload Batches" value={statisticsState.statistics.totalUploadBatches} />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic title="Total Patients" value={statisticsState.statistics.totalPatients} />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Button 
                  icon={<RefreshIcon />} 
                  onClick={() => dispatch(getDataManagementStatistics())}
                  loading={statisticsState.loading}
                >
                  Refresh
                </Button>
              </Col>
            </Row>
            {statisticsState.statistics.oldestSubject && (
              <>
                <Divider />
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Text type="secondary">Oldest Subject: </Text>
                    <Text>{moment(statisticsState.statistics.oldestSubject).format('YYYY-MM-DD HH:mm')}</Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text type="secondary">Newest Subject: </Text>
                    <Text>{moment(statisticsState.statistics.newestSubject).format('YYYY-MM-DD HH:mm')}</Text>
                  </Col>
                </Row>
              </>
            )}
            {statisticsState.statistics.oldestUploadBatch && (
              <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                <Col xs={24} sm={12}>
                  <Text type="secondary">Oldest Upload Batch: </Text>
                  <Text>{moment(statisticsState.statistics.oldestUploadBatch).format('YYYY-MM-DD HH:mm')}</Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary">Newest Upload Batch: </Text>
                  <Text>{moment(statisticsState.statistics.newestUploadBatch).format('YYYY-MM-DD HH:mm')}</Text>
                </Col>
              </Row>
            )}
          </Card>
        )}

        {statisticsState.error && (
          <Alert
            message="Error"
            description={statisticsState.error}
            type="error"
            showIcon
            closable
            onClose={() => {}}
          />
        )}

        <Card title="Clear Subjects (Cascaded)">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {clearSubjectsState.success && (
              <Alert
                message="Success"
                description={clearSubjectsState.message}
                type="success"
                showIcon
                closable
                onClose={() => dispatch(resetClearSubjects())}
              />
            )}
            {clearSubjectsState.error && (
              <Alert
                message="Error"
                description={clearSubjectsState.error}
                type="error"
                showIcon
                closable
                onClose={() => dispatch(resetClearSubjects())}
              />
            )}
            {previewSubjectsState.error && (
              <Alert
                message="Error"
                description={previewSubjectsState.error}
                type="error"
                showIcon
                closable
                onClose={() => dispatch(resetPreviewSubjects())}
              />
            )}

            <Text>Select a date range to delete subjects. Related data will be automatically deleted.</Text>
            
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Quick Select:</Text>
              <Space wrap>
                <Button size="small" onClick={() => setDateRangeShortcut('24h', setSubjectDateRange)}>
                  Last 24 Hours
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('7d', setSubjectDateRange)}>
                  Last 7 Days
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('30d', setSubjectDateRange)}>
                  Last 30 Days
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('90d', setSubjectDateRange)}>
                  Last 90 Days
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('month', setSubjectDateRange)}>
                  This Month
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('lastMonth', setSubjectDateRange)}>
                  Last Month
                </Button>
              </Space>
            </Space>

            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              value={subjectDateRange}
              onChange={setSubjectDateRange}
            />
            
            {previewSubjectsState.count !== null && (
              <Alert
                message={`${previewSubjectsState.count} subjects will be deleted`}
                type="info"
                showIcon
              />
            )}
            <Space>
              <Button 
                onClick={handlePreviewSubjects} 
                disabled={!subjectDateRange}
                loading={previewSubjectsState.loading}
              >
                Preview Count
              </Button>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleClearSubjects}
                loading={clearSubjectsState.loading}
                disabled={!subjectDateRange}
              >
                Clear Subjects
              </Button>
            </Space>
          </Space>
        </Card>

        <Card title="Clear Upload Batches (Cascaded)">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {clearUploadBatchesState.success && (
              <Alert
                message="Success"
                description={clearUploadBatchesState.message}
                type="success"
                showIcon
                closable
                onClose={() => dispatch(resetClearUploadBatches())}
              />
            )}
            {clearUploadBatchesState.error && (
              <Alert
                message="Error"
                description={clearUploadBatchesState.error}
                type="error"
                showIcon
                closable
                onClose={() => dispatch(resetClearUploadBatches())}
              />
            )}
            {previewUploadBatchesState.error && (
              <Alert
                message="Error"
                description={previewUploadBatchesState.error}
                type="error"
                showIcon
                closable
                onClose={() => dispatch(resetPreviewUploadBatches())}
              />
            )}

            <Text>Select a date range to delete upload batches. Related data will be automatically deleted.</Text>
            
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Quick Select:</Text>
              <Space wrap>
                <Button size="small" onClick={() => setDateRangeShortcut('24h', setUploadBatchDateRange)}>
                  Last 24 Hours
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('7d', setUploadBatchDateRange)}>
                  Last 7 Days
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('30d', setUploadBatchDateRange)}>
                  Last 30 Days
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('90d', setUploadBatchDateRange)}>
                  Last 90 Days
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('month', setUploadBatchDateRange)}>
                  This Month
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('lastMonth', setUploadBatchDateRange)}>
                  Last Month
                </Button>
              </Space>
            </Space>

            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              value={uploadBatchDateRange}
              onChange={setUploadBatchDateRange}
            />
            
            {previewUploadBatchesState.count !== null && (
              <Alert
                message={`${previewUploadBatchesState.count} upload batches will be deleted`}
                type="info"
                showIcon
              />
            )}
            <Space>
              <Button 
                onClick={handlePreviewUploadBatches} 
                disabled={!uploadBatchDateRange}
                loading={previewUploadBatchesState.loading}
              >
                Preview Count
              </Button>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleClearUploadBatches}
                loading={clearUploadBatchesState.loading}
                disabled={!uploadBatchDateRange}
              >
                Clear Upload Batches
              </Button>
            </Space>
          </Space>
        </Card>

        <Card title="Clear Patients (Cascaded)">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {clearPatientsState.success && (
              <Alert
                message="Success"
                description={clearPatientsState.message}
                type="success"
                showIcon
                closable
                onClose={() => dispatch(resetClearPatients())}
              />
            )}
            {clearPatientsState.error && (
              <Alert
                message="Error"
                description={clearPatientsState.error}
                type="error"
                showIcon
                closable
                onClose={() => dispatch(resetClearPatients())}
              />
            )}
            {previewPatientsState.error && (
              <Alert
                message="Error"
                description={previewPatientsState.error}
                type="error"
                showIcon
                closable
                onClose={() => dispatch(resetPreviewPatients())}
              />
            )}

            <Text>Select a date range to delete patients. Related data will be automatically deleted.</Text>
            
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Quick Select:</Text>
              <Space wrap>
                <Button size="small" onClick={() => setDateRangeShortcut('24h', setPatientDateRange)}>
                  Last 24 Hours
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('7d', setPatientDateRange)}>
                  Last 7 Days
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('30d', setPatientDateRange)}>
                  Last 30 Days
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('90d', setPatientDateRange)}>
                  Last 90 Days
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('month', setPatientDateRange)}>
                  This Month
                </Button>
                <Button size="small" onClick={() => setDateRangeShortcut('lastMonth', setPatientDateRange)}>
                  Last Month
                </Button>
              </Space>
            </Space>

            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              value={patientDateRange}
              onChange={setPatientDateRange}
            />
            
            {previewPatientsState.count !== null && (
              <Alert
                message={`${previewPatientsState.count} patients will be deleted`}
                type="info"
                showIcon
              />
            )}
            <Space>
              <Button 
                onClick={handlePreviewPatients} 
                disabled={!patientDateRange}
                loading={previewPatientsState.loading}
              >
                Preview Count
              </Button>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleClearPatients}
                loading={clearPatientsState.loading}
                disabled={!patientDateRange}
              >
                Clear Patients
              </Button>
            </Space>
          </Space>
        </Card>
      </Space>

      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <span>Confirm Deletion</span>
          </Space>
        }
        open={subjectModalVisible}
        onOk={handleConfirmClearSubjects}
        onCancel={() => setSubjectModalVisible(false)}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={clearSubjectsState.loading}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Are you sure you want to delete subjects in this date range?</Text>
          <Text strong>This action cannot be undone!</Text>
          {previewSubjectsState.count !== null && (
            <Text type="danger">Approximately {previewSubjectsState.count} subjects will be deleted.</Text>
          )}
        </Space>
      </Modal>

      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <span>Confirm Deletion</span>
          </Space>
        }
        open={uploadBatchModalVisible}
        onOk={handleConfirmClearUploadBatches}
        onCancel={() => setUploadBatchModalVisible(false)}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={clearUploadBatchesState.loading}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Are you sure you want to delete upload batches in this date range?</Text>
          <Text strong>This action cannot be undone!</Text>
          {previewUploadBatchesState.count !== null && (
            <Text type="danger">Approximately {previewUploadBatchesState.count} upload batches will be deleted.</Text>
          )}
        </Space>
      </Modal>

      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <span>Confirm Deletion</span>
          </Space>
        }
        open={patientModalVisible}
        onOk={handleConfirmClearPatients}
        onCancel={() => setPatientModalVisible(false)}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={clearPatientsState.loading}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Are you sure you want to delete patients in this date range?</Text>
          <Text strong>This action cannot be undone!</Text>
          {previewPatientsState.count !== null && (
            <Text type="danger">Approximately {previewPatientsState.count} patients will be deleted.</Text>
          )}
        </Space>
      </Modal>
    </PageWrapper>
  );
};

export default DataManagementScreen;
